import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import BbiShell from "../components/BbiShell";

const STEPS = [
  ["1", "Your Account Is Created", "We set up your brand account with a login ID & password within 24 hours. You share your story, photos and store links — that's it."],
  ["2", "Verified Page Goes Live", "Your brand gets a public page with verified badge, structured data and search-engine markup, submitted to Google for indexing."],
  ["3", "AI Articles Published", "Every month, AI writes and publishes keyword-rich articles about your brand — each one links back to your pages and builds search presence."],
  ["4", "Watch Visibility Grow", "Log in anytime with your ID & password to see page views, article views, customer enquiries — and publish new articles yourself."],
];

const PILLARS = [
  ["Verified Brand Page", "A dedicated SEO page for your brand with story, store links and schema markup Google understands."],
  ["AI SEO Articles", "Keyword-rich buying guides written by AI about YOUR brand — published, indexed and linked to your stores."],
  ["Google + AI Visibility", "Sitemap, structured data and llms.txt so your brand surfaces on Google, ChatGPT, Gemini & Perplexity."],
  ["Customer Enquiries", "Visitors on your page leave their phone & email — delivered straight to your inbox and portal."],
];

export default function BbiHowItWorks() {
  useEffect(() => {
    document.title = "How It Works — BrandsByIndia | Brand Visibility on Google & AI Search";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "How BrandsByIndia increases your brand's visibility: verified page, monthly AI SEO articles, structured data, client portal and customer enquiries — all on a simple ID & password.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <BbiShell>
      <section className="pt-14 sm:pt-20 pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h1 className="font-mkt-display text-4xl sm:text-5xl font-semibold tracking-tighter text-white" id="how">
            How <span className="mkt-gradient-text-vivid">BrandsByIndia</span> works
          </h1>
          <p className="text-sm sm:text-base text-slate-400 mt-3 max-w-2xl">
            No technical setup needed from your side — everything runs on a simple ID &amp; password.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {STEPS.map(([n, t, d]) => (
              <div key={n} className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <div className="text-4xl font-bold mkt-gradient-text-vivid">{n}</div>
                <div className="text-white font-semibold text-sm mt-3">{t}</div>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-[320px,1fr] gap-8 items-center mt-14">
            <img src="/bbi/growth.jpg" alt="Brand visibility growth on Google and AI search" loading="lazy"
              className="hidden lg:block w-full rounded-2xl border border-white/10 shadow-xl shadow-fuchsia-500/10" />
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-7">
              <div className="text-white font-semibold">Why this increases your visibility</div>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">
                Google ranks brands that have consistent, structured, quality content. BrandsByIndia builds exactly
                that: a verified page with schema markup, fresh AI articles targeting the searches your customers
                actually type, automatic sitemap submission, and machine-readable data that AI assistants like
                ChatGPT &amp; Gemini can read and cite. More indexed pages → more brand-name searches won → more
                customers finding you.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14" data-testid="bbi-what-we-do">
            {PILLARS.map(([t, d]) => (
              <div key={t} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center mb-4">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="text-white font-semibold text-sm">{t}</div>
                <p className="text-xs text-slate-400 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 bg-gradient-to-r from-indigo-500/10 to-fuchsia-500/10 border border-indigo-400/20 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <div className="text-white font-semibold text-lg">Ready to get found?</div>
              <p className="text-sm text-slate-400 mt-1">₹999 per box/month including GST — live within 24 hours.</p>
            </div>
            <Link to="/brands/pricing" data-testid="how-cta-pricing"
              className="px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-semibold transition-colors whitespace-nowrap flex items-center gap-2"
              style={{ color: "#FFFFFF" }}>
              See Pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </BbiShell>
  );
}
